import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Card, Callout, Spinner, Badge, TextField, Select, Table, Gauge, CopyField, Modal, type Column, type SelectOption } from "@/design/components";
import { ApiError } from "@/api/client";
import {
  listOperatorMailboxes,
  rotateOperatorMailbox,
  setOperatorMailboxQuota,
  setOperatorMailboxStatus,
  deleteOperatorMailbox,
  type OperatorMailbox,
  type MailAccountStatus,
} from "@/api/operator/mail";
import styles from "./OperatorMailScreen.module.css";

const PAGE = 25;
const MB = 1_048_576;
const MAX_MB = 10_240; // 10 GB — matches the API's quota ceiling.

const STATUS_OPTIONS: SelectOption[] = [
  { value: "", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
];
const STATUS_TONE: Record<MailAccountStatus, "success" | "neutral"> = { active: "success", suspended: "neutral" };
const STATUS_LABEL: Record<MailAccountStatus, string> = { active: "Active", suspended: "Suspended" };

const apiMessage = (err: unknown, fallback: string): string => (err instanceof ApiError ? err.message : fallback);
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
function formatBytes(n: number): string {
  if (n >= 1_073_741_824) return `${(n / 1_073_741_824).toFixed(n % 1_073_741_824 === 0 ? 0 : 1)} GB`;
  if (n >= MB) return `${Math.round(n / MB)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

/** A rotated password to show the operator exactly once. */
interface Revealed {
  address: string;
  password: string;
}

/**
 * Operator (cross-org) mailboxes — the god-mode counterpart to the per-org MailScreen.
 * Platform staff see every tenant's mailboxes in one table (with the owning Org), watch
 * disk usage, and can rotate (audited, reason required), re-quota, suspend/resume, or
 * delete a mailbox. There is NO create here — provisioning stays with the org admin +
 * the relay auto-provision path (least privilege). Mirrors OperatorOrgsScreen (keyset
 * "Load more" + filters) and MailScreen (row actions + one-time password reveal).
 */
export function OperatorMailScreen() {
  const qc = useQueryClient();
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [orgIdInput, setOrgIdInput] = useState("");
  const [orgId, setOrgId] = useState("");
  const [status, setStatus] = useState("");
  // Debounce the free-text inputs so each keystroke doesn't refetch.
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);
  useEffect(() => {
    const t = setTimeout(() => setOrgId(orgIdInput.trim()), 300);
    return () => clearTimeout(t);
  }, [orgIdInput]);

  const query = useInfiniteQuery({
    queryKey: ["operator", "mailboxes", q, orgId, status],
    queryFn: ({ pageParam, signal }) =>
      listOperatorMailboxes(
        {
          q: q || undefined,
          orgId: orgId || undefined,
          status: (status || undefined) as MailAccountStatus | undefined,
          before: pageParam,
          limit: PAGE,
        },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
  const hasFilters = q !== "" || orgId !== "" || status !== "";
  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.rows) ?? [], [query.data]);
  const hasLoaded = query.data !== undefined;

  const [rotateFor, setRotateFor] = useState<OperatorMailbox | null>(null);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [quotaFor, setQuotaFor] = useState<OperatorMailbox | null>(null);
  const [toDelete, setToDelete] = useState<OperatorMailbox | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["operator", "mailboxes"] });

  const rotate = useMutation({
    mutationFn: (v: { mb: OperatorMailbox; reason: string }) =>
      rotateOperatorMailbox(v.mb.id, v.reason).then((r) => ({ address: v.mb.address, password: r.password })),
    onSuccess: (r) => {
      invalidate();
      setRotateFor(null);
      setRevealed(r);
    },
  });

  const quota = useMutation({
    mutationFn: (v: { id: string; quotaBytes: number }) => setOperatorMailboxQuota(v.id, v.quotaBytes),
    onSuccess: () => {
      invalidate();
      setQuotaFor(null);
    },
  });

  const statusM = useMutation({
    mutationFn: (v: { id: string; status: MailAccountStatus }) => setOperatorMailboxStatus(v.id, v.status),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteOperatorMailbox(id),
    onSuccess: () => {
      invalidate();
      setToDelete(null);
    },
  });

  const columns: Column<OperatorMailbox>[] = [
    {
      key: "org",
      header: "Org",
      // Org name is NOT unique (only the id is) — two orgs can share a name (e.g. two
      // "Kindgeek"s). Show a short org id under the name so duplicates are distinguishable.
      render: (m) => (
        <div className={styles.orgCell}>
          <Link className={styles.orgName} to={`/operator/app/orgs/${m.orgId}`}>
            {m.orgName}
          </Link>
          <span className={styles.orgId} title={m.orgId}>
            {m.orgId.slice(0, 8)}
          </span>
        </div>
      ),
    },
    { key: "address", header: "Address", render: (m) => <span className={styles.address}>{m.address}</span> },
    {
      key: "peer",
      header: "Peer",
      render: (m) => (
        <span className={styles.mono}>
          {m.peerId}
          {m.channelName ? ` · ${m.channelName}` : ""}
        </span>
      ),
    },
    { key: "status", header: "Status", render: (m) => <Badge tone={STATUS_TONE[m.status]}>{STATUS_LABEL[m.status]}</Badge> },
    {
      key: "storage",
      header: "Storage",
      render: (m) => (
        <div className={styles.gaugeCell}>
          <Gauge label={`Quota ${formatBytes(m.quotaBytes)}`} used={m.usedBytes} limit={m.quotaBytes} unit="bytes" />
        </div>
      ),
    },
    { key: "created", header: "Created", render: (m) => <span className={styles.muted}>{formatDate(m.createdAt)}</span> },
    {
      key: "actions",
      header: "",
      render: (m) => (
        <div className={styles.rowActions}>
          <Button variant="secondary" size="sm" onClick={() => { quota.reset(); setQuotaFor(m); }}>
            Quota
          </Button>
          <Button variant="secondary" size="sm" onClick={() => { rotate.reset(); setRotateFor(m); }}>
            Rotate
          </Button>
          <Button
            variant="ghost"
            size="sm"
            loading={statusM.isPending && statusM.variables?.id === m.id}
            onClick={() => statusM.mutate({ id: m.id, status: m.status === "active" ? "suspended" : "active" })}
          >
            {m.status === "active" ? "Suspend" : "Resume"}
          </Button>
          <Button variant="danger" size="sm" onClick={() => { remove.reset(); setToDelete(m); }}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <header>
        <p className="u-eyebrow">Platform</p>
        <h1 className={styles.h1}>Mailboxes</h1>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.search}>
          <TextField label="Search" placeholder="Address or peer id…" value={qInput} onChange={(e) => setQInput(e.target.value)} />
        </div>
        <div className={styles.org}>
          <TextField label="Org id" placeholder="Filter by org id" mono value={orgIdInput} onChange={(e) => setOrgIdInput(e.target.value)} />
        </div>
        <div className={styles.filter}>
          <Select label="Status" options={STATUS_OPTIONS} value={status} onChange={(e) => setStatus(e.target.value)} />
        </div>
      </div>

      {query.isPending && (
        <div className={styles.loading}>
          <Spinner size={20} color="var(--accent)" /> <span>Loading mailboxes…</span>
        </div>
      )}
      {query.isError && (
        <Callout tone="danger" title="Couldn't load mailboxes">{apiMessage(query.error, "Please try again shortly.")}</Callout>
      )}
      {hasLoaded && !query.isError && (
        <Card className={styles.tableCard}>
          <Table
            columns={columns}
            rows={rows}
            getRowKey={(m) => m.id}
            emptyText={hasFilters ? "No mailboxes match your filters." : "No mailboxes yet."}
          />
        </Card>
      )}
      {query.hasNextPage && (
        <div className={styles.loadMore}>
          <Button variant="secondary" onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>
            Load more
          </Button>
        </div>
      )}

      {statusM.isError && (
        <div role="alert">
          <Callout tone="danger">{apiMessage(statusM.error, "Couldn't change the mailbox status.")}</Callout>
        </div>
      )}

      {/* Rotate — reason required (audited); reveals the new password once. */}
      <Modal open={rotateFor !== null} onClose={() => { if (!rotate.isPending) setRotateFor(null); }} title="Rotate mailbox password">
        {rotateFor && (
          <RotateForm
            address={rotateFor.address}
            pending={rotate.isPending}
            error={rotate.isError ? apiMessage(rotate.error, "Couldn't rotate the password.") : null}
            onCancel={() => { if (!rotate.isPending) setRotateFor(null); }}
            onSubmit={(reason) => rotate.mutate({ mb: rotateFor, reason })}
          />
        )}
      </Modal>

      {/* One-time password reveal. */}
      <Modal
        open={revealed !== null}
        onClose={() => setRevealed(null)}
        title="Password rotated"
        footer={<Button onClick={() => setRevealed(null)}>Done</Button>}
      >
        {revealed && (
          <div className={styles.reveal}>
            <Callout tone="warning" title="Copy this password now">
              This password is shown <strong>once</strong> and is never stored. Give it to the agent — if you lose it, rotate
              the mailbox again to get a new one.
            </Callout>
            <CopyField label="Address" value={revealed.address} />
            <CopyField label="Password" value={revealed.password} />
          </div>
        )}
      </Modal>

      {/* Quota edit (MB). */}
      <Modal open={quotaFor !== null} onClose={() => { if (!quota.isPending) setQuotaFor(null); }} title="Change quota">
        {quotaFor && (
          <QuotaForm
            current={quotaFor.quotaBytes}
            pending={quota.isPending}
            error={quota.isError ? apiMessage(quota.error, "Couldn't change the quota.") : null}
            onCancel={() => { if (!quota.isPending) setQuotaFor(null); }}
            onSubmit={(quotaBytes) => quota.mutate({ id: quotaFor.id, quotaBytes })}
          />
        )}
      </Modal>

      {/* Delete confirm. */}
      <Modal
        open={toDelete !== null}
        onClose={() => { if (!remove.isPending) setToDelete(null); }}
        title="Delete mailbox?"
        footer={
          <>
            <Button variant="ghost" onClick={() => { if (!remove.isPending) setToDelete(null); }} disabled={remove.isPending}>
              Cancel
            </Button>
            <Button variant="danger" loading={remove.isPending} onClick={() => toDelete && remove.mutate(toDelete.id)}>
              Delete
            </Button>
          </>
        }
      >
        <p>
          Delete <strong>{toDelete?.address}</strong>? The mailbox and everything in it are removed from the mail server.
          This can't be undone.
        </p>
        {remove.isError && <Callout tone="danger">{apiMessage(remove.error, "Couldn't delete the mailbox.")}</Callout>}
      </Modal>
    </div>
  );
}

// --- Rotate form (RHF + Zod) — reason is required + saved to the operator audit log. ---
const rotateSchema = z.object({
  reason: z.string().trim().min(3, "A reason (min 3 characters) is required"),
});
type RotateValues = z.infer<typeof rotateSchema>;

export function RotateForm({
  address,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  address: string;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}) {
  const { register, handleSubmit, formState: { errors } } = useForm<RotateValues>({
    resolver: zodResolver(rotateSchema),
    defaultValues: { reason: "" },
  });
  return (
    <form className={styles.form} onSubmit={handleSubmit((v) => onSubmit(v.reason.trim()))} noValidate>
      {error && <div role="alert"><Callout tone="danger">{error}</Callout></div>}
      <p className={styles.muted}>
        Rotating <strong>{address}</strong> issues a new password (shown once) and is recorded in the operator audit log.
        State why.
      </p>
      <label className={styles.textareaLabel}>
        <span>Reason (required — saved for the audit trail)</span>
        <textarea className={styles.textarea} rows={3} {...register("reason")} />
        {errors.reason && <span className={styles.fieldError} role="alert">{errors.reason.message}</span>}
      </label>
      <div className={styles.formActions}>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>Cancel</Button>
        <Button type="submit" loading={pending}>Rotate password</Button>
      </div>
    </form>
  );
}

// --- Quota form (MB) — bounded 1 MB .. 10 GB (the API's accepted range). Kept as a
//     string field (like RefundForm) so the RHF resolver input/output types stay aligned. ---
const quotaSchema = z.object({
  megabytes: z
    .string()
    .trim()
    .min(1, "A quota in MB is required")
    .refine((v) => /^\d+$/.test(v), "Whole number of MB")
    .refine((v) => {
      const n = Number(v);
      return n >= 1 && n <= MAX_MB;
    }, "Between 1 and 10240 MB (10 GB)"),
});
type QuotaValues = z.infer<typeof quotaSchema>;

export function QuotaForm({
  current,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  current: number;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (quotaBytes: number) => void;
}) {
  const { register, handleSubmit, formState: { errors } } = useForm<QuotaValues>({
    resolver: zodResolver(quotaSchema),
    defaultValues: { megabytes: String(Math.max(1, Math.round(current / MB))) },
  });
  return (
    <form className={styles.form} onSubmit={handleSubmit((v) => onSubmit(Number(v.megabytes) * MB))} noValidate>
      {error && <div role="alert"><Callout tone="danger">{error}</Callout></div>}
      <TextField
        label="Mailbox quota (MB)"
        type="number"
        min={1}
        max={MAX_MB}
        hint="Between 1 MB and 10240 MB (10 GB)."
        error={errors.megabytes?.message}
        {...register("megabytes")}
      />
      <div className={styles.formActions}>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>Cancel</Button>
        <Button type="submit" loading={pending}>Save quota</Button>
      </div>
    </form>
  );
}
